import { useEffect, useRef } from 'react';
import { Viewer } from '../potree/viewer/viewer';
import { createVector2, createVector3 } from '../utils/potreeThree';

interface NavigationControlsOptions {
  viewer: Viewer | undefined;
  enabled?: boolean;
}

export const useNavigationControls = ({ viewer: viewerProp, enabled = true }: NavigationControlsOptions) => {
  const wasdMoveSpeedRef = useRef<number>(10);
  const orbitMoveSpeedRef = useRef<number>(10);
  const cleanupRef = useRef<(() => void) | null>(null);
  const viewerRef = useRef<Viewer | undefined>(undefined);

  useEffect(() => {
    viewerRef.current = viewerProp;
  }, [viewerProp]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !enabled) return;
    if (!viewer.fpControls || !viewer.orbitControls || !viewer.inputHandler) return;

    // Using keyCode values instead of event.code strings
    const wasdKeyCodes = new Set([87, 65, 83, 68, 82, 70]); // W, A, S, D, R, F

    // Initialize move speeds
    wasdMoveSpeedRef.current = viewer.getMoveSpeed();
    orbitMoveSpeedRef.current = viewer.getMoveSpeed();

    // Check if we're in 2D mode (orthographic camera)
    const is2DMode = () => {
      const camera = viewer.scene?.getActiveCamera();
      return camera && 'isOrthographicCamera' in camera;
    };

    // Function to switch controls
    const switchToWASDControls = () => {
      // Don't switch to WASD controls in 2D mode
      if (is2DMode()) {
        return;
      }

      if (viewer.controls !== viewer.fpControls) {
        // Store current orbit speed before switching
        orbitMoveSpeedRef.current = viewer.getMoveSpeed();

        // IMPORTANT: Force end any ongoing orbit rotation by simulating mouse up
        // This prevents pivot shifting when switching from orbit drag to WASD
        if (viewer.orbitControls) {
          // Stop any ongoing rotation deltas
          viewer.orbitControls.stop();

          // Dispatch a 'drop' event to properly end any drag state
          viewer.orbitControls.dispatchEvent({
            type: 'drop',
            drag: null,
            viewer: viewer,
          });
        }

        viewer.setControls(viewer.fpControls);
        viewer.fpControls.lockElevation = false;

        // Restore WASD move speed
        viewer.setMoveSpeed(wasdMoveSpeedRef.current);

        // Hide cursor and enable pointer lock
        viewer.renderer.domElement.style.cursor = 'none';
        viewer.renderer.domElement.requestPointerLock();
      }
    };

    const switchToOrbitControls = () => {
      if (viewer.controls !== viewer.orbitControls) {
        // Store current WASD speed before switching (only if we were in WASD mode)
        if (viewer.controls === viewer.fpControls) {
          wasdMoveSpeedRef.current = viewer.getMoveSpeed();
        }

        viewer.setControls(viewer.orbitControls);

        // Restore orbit move speed
        viewer.setMoveSpeed(orbitMoveSpeedRef.current);

        // Show cursor and exit pointer lock
        viewer.renderer.domElement.style.cursor = '';
        if (document.pointerLockElement === viewer.renderer.domElement) {
          document.exitPointerLock();
        }

        // Recalibrate pivot point to closest intersection in front of camera
        setTimeout(() => {
          if (viewer.scene && viewer.scene.pointclouds && viewer.scene.pointclouds.length > 0) {
            const centerMouse = createVector2(viewer.renderer.domElement.clientWidth / 2, viewer.renderer.domElement.clientHeight / 2);

            // Set pivot point without moving camera - find intersection along camera's forward vector
            const intersection = viewer.inputHandler.getMousePointCloudIntersection(centerMouse);
            const view = viewer.scene.view;

            if (intersection && !Array.isArray(intersection)) {
              const newPivotLocation = intersection.location;

              // Keep camera position exactly the same, just update the radius
              // The pivot is calculated as: position + direction * radius
              // So we need: radius = distance from camera to intersection point along the view direction
              const cameraToIntersection = createVector3().subVectors(newPivotLocation, view.position);
              const distanceAlongViewDirection = cameraToIntersection.dot(view.direction.normalize());

              // Set the radius to this distance (ensuring it's positive and reasonable)
              view.radius = Math.max(0.1, distanceAlongViewDirection);
            } else {
              // No intersection found - set a default pivot point in front of the camera
              // Use a reasonable distance based on current move speed or a default value
              const defaultDistance = Math.max(10, viewer.getMoveSpeed() / 10); // Dynamic based on move speed
              view.radius = defaultDistance;
            }
          }
        }, 100); // Small delay to ensure control switch is complete
      }
    };

    // Check if any WASD key is currently pressed
    const checkWASDKeyPressed = () => {
      return Array.from(wasdKeyCodes).some((keyCode) => viewer.inputHandler.pressedKeys[keyCode]);
    };

    // Handle mouse movement in pointer lock mode
    const handleMouseMove = (e: MouseEvent) => {
      if (document.pointerLockElement === viewer.renderer.domElement && viewer.controls === viewer.fpControls) {
        // Get movement data from pointer lock
        const movementX = e.movementX || 0;
        const movementY = e.movementY || 0;

        // Convert to normalized coordinates and feed directly into FirstPersonControls
        // This mimics the drag calculation in FirstPersonControls.js lines 65-72
        const ndrag = {
          x: movementX / viewer.renderer.domElement.clientWidth,
          y: movementY / viewer.renderer.domElement.clientHeight,
        };

        // Apply rotation using the same logic as FirstPersonControls
        viewer.fpControls.yawDelta += ndrag.x * viewer.fpControls.rotationSpeed;
        viewer.fpControls.pitchDelta += ndrag.y * viewer.fpControls.rotationSpeed;
      }
    };

    // Handle pointer lock change events
    const handlePointerLockChange = () => {
      if (!document.pointerLockElement && viewer.controls === viewer.fpControls) {
        // Pointer lock was exited, switch back to orbit controls
        switchToOrbitControls();
      }
    };

    // Handle escape key to exit WASD mode
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        // Escape key
        switchToOrbitControls();
      }
    };

    // Handle mouse events for control switching
    const handleMouseDown = () => {
      // Don't switch to WASD controls in 2D mode
      if (is2DMode()) {
        return;
      }

      if (checkWASDKeyPressed()) {
        switchToWASDControls();
      }
    };

    const handleMouseUp = () => {
      // Switch back to orbit controls when mouse is released
      switchToOrbitControls();
    };

    const handleKeyDownForWASD = (e: KeyboardEvent) => {
      // Don't switch to WASD controls in 2D mode
      if (is2DMode()) {
        return;
      }

      // Convert key code using modern approach
      // Only check for single character keys (WASD, R, F)
      if (e.key && e.key.length === 1) {
        const keyCode = e.key.toUpperCase().charCodeAt(0);
        if (wasdKeyCodes.has(keyCode) && viewer.inputHandler.mouseDown) {
          switchToWASDControls();
        }
      }
    };

    // Add event listeners
    viewer.renderer.domElement.addEventListener('mousemove', handleMouseMove);
    viewer.renderer.domElement.addEventListener('mousedown', handleMouseDown);
    viewer.renderer.domElement.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keydown', handleKeyDownForWASD);
    document.addEventListener('pointerlockchange', handlePointerLockChange);

    // Start with orbit controls as default
    switchToOrbitControls();

    // Store cleanup function
    cleanupRef.current = () => {
      // Remove event listeners
      viewer.renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      viewer.renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      viewer.renderer.domElement.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keydown', handleKeyDownForWASD);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);

      // Ensure we exit pointer lock and restore cursor when cleaning up
      if (document.pointerLockElement === viewer.renderer.domElement) {
        document.exitPointerLock();
      }
      viewer.renderer.domElement.style.cursor = '';
    };

    // Cleanup on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [viewerProp, enabled]);

  return {
    cleanup: () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    },
  };
};
